import { Bot, CircleX, History, Info, Layers, Play } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { Banner, Button, EmptyState, Panel, Segmented, Select, Skeleton, StatStrip, StatTile, Tooltip, formatInt, useNow } from '@orgus/ui';
import { AgentsGlance } from '../features/agents/AgentsGlance.tsx';
import { LiveRoom } from '../features/agents/LiveRoom.tsx';
import { agentsGlance } from '../features/agents/glance.ts';
import { RunDrawer } from '../features/agents/RunDrawer.tsx';
import { TimelineKey } from '../features/agents/ActivityLegend.tsx';
import { Timeline } from '../features/agents/Timeline.tsx';
import { NO_PROJECT, WINDOWS, WINDOW_KEYS, buildLanes, countRuns, findRun, isWindowKey, projectKey } from '../features/agents/model.ts';
import type { RunFilter, TimelineRun, WindowKey } from '../features/agents/model.ts';
import { usePageKeys } from '../features/agents/usePageKeys.ts';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { useOrgus, useSessions } from '../lib/store.ts';
import { machineLiveness } from '@orgus/protocol';

const FILTER_LABEL: Record<RunFilter, string> = { all: 'All', running: 'Running', failed: 'Failed' };

function TimelineSkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading the timeline">
      <StatStrip label="Loading agent counts" className="lg:grid-cols-4">
        {['Subagents running', 'Workflows running', 'Runs', 'Failed'].map((l) => (
          <StatTile key={l} label={l} value={0} loading />
        ))}
      </StatStrip>
      <Panel flush>
        <div className="flex h-filter-bar items-center gap-4 px-3 pt-3">
          <Skeleton className="h-control-sm w-40" />
          <Skeleton className="h-control-sm w-48" />
          <Skeleton className="ml-auto h-control-sm w-32" />
        </div>
        <div className="flex flex-col gap-1 py-3">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="flex h-lane items-center">
              <span className="w-44 shrink-0 px-row-x md:w-60 lg:w-72 2xl:w-80">
                <Skeleton className="h-3 w-40" />
              </span>
              <span className="relative mr-4 h-full flex-1">
                <Skeleton className="absolute top-1/2 h-3 -translate-y-1/2" style={{ left: `${8 + ((i * 23) % 50)}%`, width: `${10 + ((i * 7) % 30)}%` }} />
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/** Agents and workflows: a live Gantt of every subagent and workflow run across sessions. */
export default function Agents() {
  const [params, setParams] = useSearchParams();
  const win: WindowKey = isWindowKey(params.get('window')) ? (params.get('window') as WindowKey) : '1h';
  const filterParam = params.get('show');
  const filter: RunFilter = filterParam === 'running' || filterParam === 'failed' ? filterParam : 'all';
  const runId = params.get('run');
  const project = params.get('project') || null;

  const now = useNow(1000);
  const everySession = useSessions();
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  const { ms, step } = WINDOWS[win];
  const start = now - ms;
  // The project filter narrows everything below it: the glance, the counts and the timeline.
  const sessions = useMemo(() => (project ? everySession.filter((s) => projectKey(s) === project) : everySession), [everySession, project]);
  // Projects with runs in the window, whatever the filters show, with their sessions counted.
  const projectOptions = useMemo(() => {
    const n = new Map<string, number>();
    for (const l of buildLanes(everySession, start, now)) n.set(projectKey(l.session), (n.get(projectKey(l.session)) ?? 0) + 1);
    if (project && !n.has(project)) n.set(project, 0);
    const name = (k: string) => (k === NO_PROJECT ? 'No project' : (projects[k]?.name ?? 'Unknown project'));
    return [...n.entries()]
      .map(([k, c]) => ({ value: k, label: `${name(k)} (${formatInt(c)})`, name: name(k) }))
      .sort((a, b) => Number(a.value === NO_PROJECT) - Number(b.value === NO_PROJECT) || a.name.localeCompare(b.name));
  }, [everySession, start, now, project, projects]);

  const lanes = useMemo(() => buildLanes(sessions, start, now, filter), [sessions, start, now, filter]);
  // The glance counts every run in the window, whatever the filter shows.
  const allLanes = useMemo(() => (filter === 'all' ? lanes : buildLanes(sessions, start, now, 'all')), [filter, lanes, sessions, start, now]);
  const glance = useMemo(() => agentsGlance(allLanes, start, now), [allLanes, start, now]);
  const counts = useMemo(() => countRuns(sessions, start, now), [sessions, start, now]);
  // ?run= names a subagent, a workflow, or one agent of a workflow (shown in its workflow's drawer).
  const found = useMemo(() => (runId ? (findRun(sessions, runId) ?? null) : null), [sessions, runId]);
  const openRun = useMemo(() => (found?.kind === 'agent' && found.workflowId ? (findRun(sessions, found.workflowId) ?? found) : found), [sessions, found]);
  const focusAgentId = found?.kind === 'agent' ? found.id : null;
  const machineState = useCallback((id: string) => {
    const m = machines[id];
    return m ? machineLiveness(m, Date.now()) : 'offline';
  }, [machines]);
  // Workflows open into their agents while they run, and always when filtered to running or failed.
  const defaultOpen = useCallback((r: TimelineRun) => filter !== 'all' || r.status === 'running', [filter]);

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setWindow = useCallback((w: WindowKey) => update({ window: w === '1h' ? null : w }), [update]);
  const setFilter = useCallback((f: RunFilter) => update({ show: f === 'all' ? null : f }), [update]);
  const setProject = useCallback((k: string) => update({ project: k || null }), [update]);
  const open = useCallback((r: TimelineRun) => update({ run: r.id }), [update]);
  const close = useCallback(() => update({ run: null }), [update]);
  // The run the drawer last showed: on close, focus goes back to its bar (the bar that opened
  // it may have re-rendered, and a deep link had no opener at all).
  const lastRunId = useRef<string | null>(null);
  useEffect(() => {
    if (runId) lastRunId.current = runId;
  }, [runId]);
  const returnFocus = useCallback((e: Event) => {
    const id = lastRunId.current;
    const bar = id ? document.querySelector<HTMLElement>(`[data-run-id="${CSS.escape(id)}"]`) : null;
    if (!bar) return;
    e.preventDefault();
    bar.focus({ preventScroll: false });
  }, []);
  // A ?run= that no reported run matches (it ended long ago, or belongs to a replaced attempt).
  const missingRun = runId != null && found == null;

  usePageKeys({ '1': () => setWindow('1h'), '2': () => setWindow('6h'), '3': () => setWindow('24h') });

  const cmds = useMemo<Command[]>(
    () => [
      ...WINDOW_KEYS.map(
        (w, i): Command => ({
          id: `agents.window.${w}`,
          title: `Show runs from ${WINDOWS[w].long}`,
          group: 'Agents & workflows',
          icon: History,
          shortcut: [String(i + 1)],
          keywords: ['timeline', 'window', w],
          order: i,
          perform: () => setWindow(w),
        }),
      ),
      { id: 'agents.filter.running', title: 'Show running subagents and workflows only', group: 'Agents & workflows', icon: Play, order: 10, perform: () => setFilter('running') },
      { id: 'agents.filter.failed', title: 'Show failed runs only', group: 'Agents & workflows', icon: CircleX, order: 11, perform: () => setFilter('failed') },
      { id: 'agents.filter.all', title: 'Show every run', group: 'Agents & workflows', icon: Layers, order: 12, perform: () => setFilter('all') },
    ],
    [setWindow, setFilter],
  );
  useRegisterCommands(cmds, [cmds]);

  return (
    <PageBody wide>
      <DataGate skeleton={<TimelineSkeleton />}>
        <div className="flex flex-col gap-6">
          <AgentsGlance g={glance} win={win} onFilter={setFilter} />
          <LiveRoom />
          <Panel flush>
            {/* Groups with room between them: which runs (project, status), then when (the window) beside the key. */}
            <div className="flex min-h-filter-bar flex-wrap items-center gap-x-4 gap-y-2 px-3 pt-3 pb-1">
              {projectOptions.length > 1 && (
                <Select
                  size="sm"
                  aria-label="Project"
                  value={project ?? ''}
                  onValueChange={setProject}
                  options={[{ value: '', label: 'All projects' }, ...projectOptions.map(({ value, label }) => ({ value, label }))]}
                  wrapperClassName="w-48"
                />
              )}
              <div className="max-w-full overflow-x-auto">
                <Segmented
                  label="Show"
                  value={filter}
                  onChange={setFilter}
                  options={(['all', 'running', 'failed'] as RunFilter[]).map((f) => ({ value: f, label: FILTER_LABEL[f], count: counts[f] }))}
                />
              </div>
              <div className="ml-auto flex items-center gap-3">
                <Tooltip content="Time window" shortcut={['1', '2', '3']}>
                  <span>
                    <Segmented label="Time window" value={win} onChange={setWindow} options={WINDOW_KEYS.map((w) => ({ value: w, label: WINDOWS[w].label }))} />
                  </span>
                </Tooltip>
                <TimelineKey />
              </div>
            </div>
            {missingRun && (
              <Banner
                icon={<Info />}
                title="That run is not in the last 24 hours"
                className="mx-3 mt-2"
                actions={
                  <Button variant="secondary" size="sm" onClick={close}>
                    Clear the link
                  </Button>
                }
              >
                It may have ended earlier, or belong to an attempt that was replaced.
              </Banner>
            )}
            {lanes.length ? (
              <div className="px-1 pb-2">
                <Timeline
                  lanes={lanes}
                  start={start}
                  end={now}
                  step={step}
                  machineName={(id) => machines[id]?.name ?? 'Unknown machine'}
                  projectName={(id) => (id ? (projects[id]?.name ?? null) : null)}
                  machineState={machineState}
                  onOpen={open}
                  activeRunId={runId}
                  defaultOpen={defaultOpen}
                />
              </div>
            ) : (
              <EmptyState
                icon={<Bot />}
                title={
                  filter === 'all'
                    ? `No subagent or workflow runs in ${WINDOWS[win].long}${project ? ' in this project' : ''}`
                    : `No ${FILTER_LABEL[filter].toLowerCase()} runs in ${WINDOWS[win].long}${project ? ' in this project' : ''}`
                }
                description="Runs appear here when a session starts a subagent or a workflow."
                action={
                  project ? (
                    <Button variant="secondary" onClick={() => setProject('')}>
                      Show every project
                    </Button>
                  ) : filter !== 'all' ? (
                    <Button variant="secondary" onClick={() => setFilter('all')}>
                      Show every run
                    </Button>
                  ) : win !== '24h' ? (
                    <Button variant="secondary" onClick={() => setWindow('24h')}>
                      Show the last 24 hours
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Panel>
        </div>
      </DataGate>
      <RunDrawer run={openRun} onClose={close} focusAgentId={focusAgentId} onCloseAutoFocus={returnFocus} />
    </PageBody>
  );
}
